import React, { useState, useEffect } from "react";
import { useLogin } from "./features/auth/hooks/useLogin";
import { useTranslator } from "./features/translator/hooks/useTranslator";
import { AuthPage } from "./features/auth/components/AuthPage";
import { AppHeader } from "./features/translator/components/AppHeader";
import { TranslationHistory } from "./features/translator/components/TranslationHistory";
import { SubtitleView } from "./features/translator/components/SubtitleView";
import { MinutaView } from "./features/translator/components/MinutaView";
import { SettingsModal } from "./features/translator/components/SettingsModal";
import { DocsModal } from "./features/translator/components/DocsModal";
import { SummaryModal } from "./features/translator/components/SummaryModal";
import { UiLangProvider, useUiLang } from "./context/UiLangContext";
import "./index.css";
import kiraLogo from "./assets/Kira.png";

// ── Inner app (must be inside UiLangProvider to use useUiLang) ───────────────
function AppInner() {
  const auth = useTranslator();
  const login = useLogin();
  const { uiLang } = useUiLang();
  const isEn = uiLang === "en";
  const [theme, setTheme] = useState<"light" | "dark">("dark");
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [docsOpen, setDocsOpen] = useState(false);
  const [summaryOpen, setSummaryOpen] = useState(false);

  // Load theme from localStorage on mount
  useEffect(() => {
    const saved = localStorage.getItem("theme") as "light" | "dark" | null;
    const initial = saved || "dark";
    setTheme(initial);
    document.documentElement.classList.toggle("dark-mode", initial === "dark");
  }, []);

  // Keyboard shortcuts: Escape closes modals
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        setDocsOpen(false);
        setSettingsOpen(false);
        setSummaryOpen(false);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  const toggleTheme = () => {
    const next = theme === "dark" ? "light" : "dark";
    setTheme(next);
    localStorage.setItem("theme", next);
    document.documentElement.classList.toggle("dark-mode", next === "dark");
  };

  const handleToggleRecording = () => {
    auth.toggleRecording(() => setSummaryOpen(true));
  };

  // ── Loading spinner while session is being restored ──────────────────────────
  if (login.isInitializing) {
    return (
      <div className={`flex items-center justify-center min-h-screen w-screen transition-all ${theme === "dark" ? "bg-[#1A1A1E]" : "bg-white"}`}>
        <div className="bg-sliding-pattern" style={{ backgroundImage: `url(${kiraLogo})` }} />
        <div className="w-10 h-10 border-4 border-[#007aff]/20 border-t-[#007aff] rounded-full animate-spin" />
      </div>
    );
  }

  // ── Auth screens ─────────────────────────────────────────────────────────────
  if (login.step !== "AUTHENTICATED") {
    return <AuthPage auth={login} theme={theme} toggleTheme={toggleTheme} />;
  }

  // ── Main app ─────────────────────────────────────────────────────────────────
  return (
    <div className="flex flex-col w-screen h-screen bg-[var(--window-bg)] text-[var(--text)] overflow-hidden font-sans relative transition-colors duration-300">
      <AppHeader
        appMode={auth.appMode}
        recording={auth.recording}
        status={auth.status}
        voiceEnabled={auth.voiceEnabled}
        pipActive={auth.pipActive}
        statusText={auth.statusText}
        srcLang={auth.srcLang}
        tgtLang={auth.tgtLang}
        theme={theme}
        meetingTranscriptLength={auth.meetingTranscript.length}
        getStatusColorClass={auth.getStatusColorClass}
        setAppMode={auth.setAppMode}
        setVoiceEnabled={auth.setVoiceEnabled}
        setSrcLang={auth.setSrcLang}
        setTgtLang={auth.setTgtLang}
        onToggleRecording={handleToggleRecording}
        onTogglePip={auth.togglePip}
        onToggleTheme={toggleTheme}
        onOpenDocs={() => setDocsOpen(true)}
        onOpenSummary={() => setSummaryOpen(true)}
        onLogout={login.handleLogout}
      />

      {/* Deepgram API key warning banner */}
      {!auth.settings.deepgramApiKey && (
        <div className="bg-yellow-500/10 border-b border-yellow-500/20 text-yellow-500 px-5 py-2 text-xs flex items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            <span>⚠️</span>
            <span>
              {isEn
                ? "Deepgram API Key not configured. Real-time transcription won't work without it."
                : "No se ha configurado la API Key de Deepgram. La transcripción en tiempo real no funcionará sin ella."}
            </span>
          </div>
          <button
            onClick={() => setSettingsOpen(true)}
            className="underline font-semibold hover:text-[var(--text)] transition-all whitespace-nowrap"
          >
            {isEn ? "Configure now" : "Configurar ahora"}
          </button>
        </div>
      )}

      {/* Main content */}
      <main className="flex-1 flex flex-col lg:flex-row overflow-hidden min-h-0 relative">
        {auth.appMode === "traductor" && (
          <TranslationHistory
            meetingTranscript={auth.meetingTranscript}
            tgtLang={auth.tgtLang}
            onSelectItem={auth.selectHistoryItem}
            onSpeak={auth.speakTranslation}
          />
        )}

        <div className="flex-1 flex flex-col min-w-0 bg-[var(--content-bg)] relative order-1 lg:order-2 h-full">
          {auth.appMode === "traductor" && (
            <SubtitleView
              subtitleOrig={auth.subtitleOrig}
              subtitleTran={auth.subtitleTran}
            />
          )}

          {auth.appMode === "minuta" && (
            <MinutaView
              meetingTranscript={auth.meetingTranscript}
              recording={auth.recording}
              onOpenSummary={() => setSummaryOpen(true)}
            />
          )}
        </div>
      </main>

      {/* Footer */}
      <footer className="h-[36px] px-5 border-t border-[var(--border)] bg-[var(--footer-bg)] flex items-center justify-between text-[10px] text-[var(--text-dim)] font-medium z-10 flex-shrink-0 transition-colors duration-300">
        <p>Kira v1.1 — Powered by HOLOCRUXE</p>
        <p className="font-mono text-[9px]">Deepgram Nova-2 · Google Translate · PiP</p>
      </footer>

      {/* Modals */}
      <SettingsModal
        isOpen={settingsOpen}
        onClose={() => setSettingsOpen(false)}
        onSave={auth.saveSettings}
        initialSettings={auth.settings}
      />

      <DocsModal
        isOpen={docsOpen}
        onClose={() => setDocsOpen(false)}
      />

      <SummaryModal
        isOpen={summaryOpen}
        onClose={() => setSummaryOpen(false)}
        meetingTranscript={auth.meetingTranscript}
        currentMeetingId={auth.currentMeetingId}
        summaryServiceUrl={auth.settings.summaryServiceUrl}
        targetLang={auth.tgtLang}
        targetLangLabel={auth.getLanguageLabel(auth.tgtLang)}
      />
    </div>
  );
}

// ── Root export: wraps everything with UiLangProvider ────────────────────────
export function App() {
  return (
    <UiLangProvider>
      <AppInner />
    </UiLangProvider>
  );
}

export default App;
